import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MarkdownPipe } from '../../../core/pipes/markdown.pipe';
import { shouldRenderMarkdown as contentLooksLikeMarkdown } from '../../../core/utils/markdown.util';
import { TestCoverageComponent, TestCoverageConfigs } from '../../modal-templates/test-coverage/test-coverage.component';
import { CodeSnippetComponent } from '../code-snippet/code-snippet.component';
import { NoDataComponent } from '../no-data/no-data.component';

@Component({
  selector: 'app-expandable-accordion',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTooltipModule, CodeSnippetComponent, NoDataComponent, MarkdownPipe],
  templateUrl: './expandable-accordion.component.html',
  styleUrl: './expandable-accordion.component.scss'
})
export class ExpandableAccordionComponent implements OnChanges {
  private readonly modalService = inject(NgbModal);

  @Input() data: any;
  @Input() templateId: string = 'default';
  @Input() showSelectAll: boolean = true;
  @Input() showHeader: boolean = true;
  /** When true, select-all and row checkboxes are disabled (non-interactive). */
  @Input() selectionDisabled: boolean = false;
  @Input() showHeaderActions: boolean = false;
  @Input() showAddToSuiteButton: boolean = false;
  @Input() showPaginationControls: boolean = false;
  @Input() showFilterSortControls: boolean = false;
  /** Detail-view toolbar icons after pagination (filter / download / delete). */
  @Input() showFilterButton: boolean = false;
  /** Shows a dot on the filter icon when a filter is applied. */
  @Input() filterActive: boolean = false;
  @Input() showDownloadButton: boolean = false;
  @Input() showDeleteButton: boolean = false;
  @Input() showAddMissingButton: boolean = false;
  /** When true, renders "Add Missing Test Cases" as a split dropdown with Create / Import options. */
  @Input() addMissingIsDropdown: boolean = false;
  @Input() testCoverageConfigs: TestCoverageConfigs | null = null;
  @Input() hideRecommendationCount = false;
  @Input() hideCodeSnippetTitle = false;
  @Input() info: any = {
    display: false,
    tooltip: '',
  };
  @Input() descriptionImage: { prefix?: { url: string }, suffix?: { url: string }} = {};
  @Input() showRecommendations: boolean = true;
  @Input() makeTitleBold: boolean = false;
  @Input() pageSize: number = 0;
  /** When false, hides thumbs-up / thumbs-down feedback actions. */
  @Input() showFeedbackActions: boolean = true;
  /** When false, hides the clickable user-story id link and " / " separator. */
  @Input() showStoryIdLink = true;
  /**
   * When true (non-agentic), thumbs-down opens the inline feedback typing panel.
   * When false (agentic), thumbs-down only emits so the parent can route feedback via chat prompt.
   */
  @Input() showInlineFeedbackPanel = true;
  /** When true, shows an edit button in place of (or alongside) feedback actions. */
  @Input() showEditButton: boolean = false;
  /** When false, hides the expand control and keeps each row collapsed. */
  @Input() showExpandButton = true;
  /** When true, keeps the header visible and shows a no-data state if rows are empty. */
  @Input() showEmptyState = false;
  /** When true, disables Update from Feedback and shows in-progress label. */
  @Input() feedbackSubmitting = false;
  /**
   * Row ids that stay checked when the list is replaced, including rows that
   * are not in the current page of data. Used by flows that remember a
   * selection across folder changes.
   */
  @Input() retainedSelectionIds: string[] | null = null;
  @Output() actionEmit = new EventEmitter<any>();

  addMissingDropdownOpen = false;

  @HostListener('document:click')
  onDocumentClick(): void {
    this.addMissingDropdownOpen = false;
  }

  Array = Array;

  shouldRenderMarkdown(value: unknown, mapping?: string): boolean {
    return contentLooksLikeMarkdown(value, mapping);
  }

  expandedRows = new Set<number>();
  selectedRows = new Set<number>();
  isSelectAllChecked: boolean = false;
  selectedThumbsDownRow: number | null = null;
  feedbackTexts = new Map<number, string>();
  thumbsUpClicked = new Map<number, boolean>();
  thumbsDownClicked = new Map<number, boolean>();
  currentPage: number = 1;

  get totalRows(): number {
    return this.data?.rows?.length || 0;
  }

  get totalPages(): number {
    return this.pageSize > 0 ? Math.ceil(this.totalRows / this.pageSize) : 1;
  }

  get pageStart(): number {
    if (!this.totalRows) return 0;
    if (!this.pageSize || this.pageSize <= 0) return 1;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get pageEnd(): number {
    if (!this.pageSize || this.pageSize <= 0) return this.totalRows;
    return Math.min(this.currentPage * this.pageSize, this.totalRows);
  }

  isRowVisible(index: number): boolean {
    if (!this.pageSize || this.pageSize <= 0) return true;
    const start = (this.currentPage - 1) * this.pageSize;
    return index >= start && index < start + this.pageSize;
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.expandedRows.clear();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.expandedRows.clear();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      const prevRows: any[] = changes['data'].previousValue?.rows ?? [];
      const nextRows: any[] = changes['data'].currentValue?.rows ?? [];
      const isFirstLoad = !changes['data'].previousValue;

      // Rematch selection / expand state by stable row id so poll refreshes
      // (and other in-place data replacements) do not clear checkboxes.
      const selectedKeys = new Set(
        [...this.selectedRows]
          .map(i => this.rowKey(prevRows[i]))
          .filter((k): k is string => !!k)
      );
      const expandedKeys = new Set(
        [...this.expandedRows]
          .map(i => this.rowKey(prevRows[i]))
          .filter((k): k is string => !!k)
      );

      if (isFirstLoad) {
        this.currentPage = 1;
      } else if (this.pageSize > 0) {
        // Filter / delete can shrink the list while the user is on a later page
        // (e.g. page 2 → "7 - 6 of 6" empty view). Reset when out of range or list shrinks.
        const maxPage = Math.max(1, Math.ceil(nextRows.length / this.pageSize));
        if (this.currentPage > maxPage || nextRows.length < prevRows.length) {
          this.currentPage = 1;
        }
      }

      this.selectedRows.clear();
      this.expandedRows.clear();
      this.selectedThumbsDownRow = null;
      this.feedbackTexts.clear();
      this.thumbsUpClicked.clear();
      this.thumbsDownClicked.clear();

      const retainedIds = new Set(
        (this.retainedSelectionIds ?? []).filter((id) => !!id)
      );

      nextRows.forEach((row, i) => {
        const key = this.rowKey(row);
        if (!key) return;
        if (selectedKeys.has(key) || retainedIds.has(key)) this.selectedRows.add(i);
        if (expandedKeys.has(key)) this.expandedRows.add(i);
      });

      const total = nextRows.length;
      this.isSelectAllChecked = this.selectedRows.size === total && total > 0;

      // Keep parent selection in sync with refreshed row objects
      if (selectedKeys.size > 0) {
        this.emitSelection();
      }
    }
  }

  /** Stable identity for a row across poll/data refreshes. Prefer unique `_id` (BPD reuses testCaseId). */
  private rowKey(row: any): string {
    if (!row) return '';
    const key = row._id ?? row.id ?? row.testCaseId ?? row.storyId ?? row.key ?? '';
    return key !== undefined && key !== null ? String(key) : '';
  }


  get hasRowActions(): boolean {
    return this.showExpandButton || this.showEditButton || this.showFeedbackActions;
  }

  toggleRow(index: number): void {
    if (!this.showExpandButton) {
      return;
    }
    if (this.expandedRows.has(index)) {
      this.expandedRows.delete(index);
    } else {
      this.expandedRows.add(index);
    }
  }

  isExpanded(index: number): boolean {
    return this.expandedRows.has(index);
  }

  toggleSelectAll(): void {
    if (this.selectionDisabled) return;
    const total = this.data?.rows?.length ?? 0;
    if (this.isSelectAllChecked) {
      for (let i = 0; i < total; i++) this.selectedRows.add(i);
    } else {
      this.selectedRows.clear();
    }
    this.emitSelection();
  }

  toggleCheckbox(index: number): void {
    if (this.selectionDisabled) return;
    if (this.selectedRows.has(index)) {
      this.selectedRows.delete(index);
    } else {
      this.selectedRows.add(index);
    }
    const total = this.data?.rows?.length ?? 0;
    this.isSelectAllChecked = this.selectedRows.size === total && total > 0;
    this.emitSelection();
  }

  isSelected(index: number): boolean {
    return this.selectedRows.has(index);
  }

  emitSelection(): void {
    const selectedData = this.data?.rows?.filter((_: any, i: number) => this.selectedRows.has(i)) || [];
    this.actionEmit.emit({
      type: 'row_select',
      data: selectedData
    });
  }

  handleAction(actionType: string, row: any, index: number): void {
    if (actionType === 'thumbs_up') {
      const isCurrentlyClicked = this.thumbsUpClicked.get(index);
      this.thumbsUpClicked.set(index, !isCurrentlyClicked);
      this.thumbsDownClicked.set(index, false);
      // Close feedback panel if switching to thumbs-up
      if (this.selectedThumbsDownRow === index) {
        this.selectedThumbsDownRow = null;
      }
    } else if (actionType === 'thumbs_down') {
      const isCurrentlyClicked = this.thumbsDownClicked.get(index);
      const turningOn = !isCurrentlyClicked;
      this.thumbsDownClicked.set(index, turningOn);
      this.thumbsUpClicked.set(index, false);
      // Inline feedback panel only for non-agentic; agentic sends via chat prompt
      if (this.showInlineFeedbackPanel) {
        if (turningOn) {
          this.selectedThumbsDownRow = index;
          this.expandedRows.add(index);
        } else {
          this.selectedThumbsDownRow = null;
        }
      } else {
        this.selectedThumbsDownRow = null;
      }
    }

    this.actionEmit.emit({
      type: actionType,
      data: row,
      index: index
    });
  }

  getFeedbackText(index: number): string {
    return this.feedbackTexts.get(index) ?? '';
  }

  setFeedbackText(index: number, value: string): void {
    this.feedbackTexts.set(index, value);
  }

  onUpdateFromFeedback(index: number, row: any): void {
    const feedback = this.getFeedbackText(index)?.trim();
    if (!feedback || this.feedbackSubmitting) {
      return;
    }
    this.actionEmit.emit({
      type: 'feedback_submit',
      data: row,
      index,
      feedback,
    });
  }

  isThumbsUpClicked(index: number): boolean {
    return this.thumbsUpClicked.get(index) || false;
  }

  isThumbsDownClicked(index: number): boolean {
    return this.thumbsDownClicked.get(index) || false;
  }

  onHeaderAction(type: string): void {
    const selectedData = this.data?.rows?.filter((_: any, i: number) => this.selectedRows.has(i)) || [];
    if (type === 'test_coverage') {
      this.openTestCoverageModal();
      return;
    }
    this.actionEmit.emit({ type , data: selectedData});
  }

  /** Opens User Story Details modal via parent (same as generated-test-cases). */
  onStoryIdClick(event: Event, row: any): void {
    event.stopPropagation();
    this.actionEmit.emit({
      type: 'open-user-story',
      data: row,
    });
  }

  /** Hide story id for BPD-sourced rows; otherwise follow showStoryIdLink. */
  shouldShowStoryId(row: any): boolean {
    if (!this.showStoryIdLink) {
      return false;
    }
    if (String(row?.source ?? '').toUpperCase() === 'BPD') {
      return false;
    }
    return !!(row?.userStoryId || row?.key);
  }

  /**
   * A story key is only clickable (opens the popup) when the row has actual
   * story description content. An empty/blank storydesc means there is no
   * story details to display, so the key renders as plain text instead.
   */
  isStoryIdClickable(row: any): boolean {
    return !!(row?.storydesc?.trim());
  }

  private openTestCoverageModal(): void {
    if (!this.testCoverageConfigs) {
      return;
    }
    const ref = this.modalService.open(TestCoverageComponent, {
      size: 'xl',
      centered: true,
      scrollable: true,
    });
    ref.componentInstance.configs = this.testCoverageConfigs;
  }

  getStarArray(count: number): number[] {
    return Array.from({ length: 5 }, (_, i) => i + 1);
  }

  getVisibleColumns(): any[] {
    const cols = [...(this.data?.columns?.filter((col: any) => col.isVisible) || [])];
    const storyIdx = cols.findIndex((c: any) => c.mapping_key === 'userStoryId');
    const caseIdx  = cols.findIndex((c: any) => c.mapping_key === 'testCaseId');
    if (storyIdx !== -1 && caseIdx !== -1 && storyIdx > caseIdx) {
      const [storyCol] = cols.splice(storyIdx, 1);
      cols.splice(caseIdx, 0, storyCol);
    }
    return cols;
  }

  getRecommendations(data: string = '') {
    return JSON.parse(data.replace(/'/g, '"'));
  }

  getExpandableColumns(): any[] {
    return this.data?.columns?.filter((col: any) => 
      !col.isVisible && 
      col.mapping_key !== '_checkbox' && 
      col.mapping_key !== 'path' && 
      col.mapping_key !== 'priority'
    ) || [];
  }

  getPriorityClass(priority: string): string {
    switch (priority?.toUpperCase()) {
      case 'P1':
      case 'CRITICAL':
        return 'critical';
      case 'P2':
      case 'HIGH':
        return 'high';
      case 'P3':
      case 'MEDIUM':
        return 'medium';
      case 'P4':
      case 'LOW':
        return 'low';
      default:
        return '';
    }
  }

  handleQualityReportClick(row: any, index: number): void {
    this.actionEmit.emit({
      type: 'quality_report',
      data: row,
      index
    });
  }

  /** Story key used to count acceptance criteria covered in a traceability string. */
  getTraceabilityStoryKey(row: any): string {
    return row?.userStoryId ?? row?.key ?? '';
  }

  /**
   * Counts covered rules by splitting on `||` or `#`.
   * Story ids (e.g. `"BMO-3 :"`) are removed first and are not counted.
   */
  getTraceabilityCoveredCount(traceability: string, storyKey: string): number {
    if (!traceability) {
      return 0;
    }

    let text = String(traceability);
    if (storyKey) {
      const escapedKey = storyKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      text = text.replace(new RegExp(`${escapedKey}\\s*:?`, 'gi'), '');
    }

    const items = text
      .split(/#|\|\|/)
      .map(item => this.cleanListItem(item))
      .filter(item => {
        if (!item) return false;
        if (!storyKey) return true;
        return item.toLowerCase() !== storyKey.toLowerCase();
      });

    return items.length;
  }

  getTraceabilityLabel(traceability: string, storyKey: string): string {
    const count = this.getTraceabilityCoveredCount(traceability, storyKey);
    if (count > 1) {
      return `${count} Business Rules Covered`;
    } else if (count === 1){
      return `${count} Business Rule Covered`;
    }
    // BPD free-text businessRuleRef has no "STORY:" segments — still count as one rule
    return traceability?.trim() ? '1 Business Rule Covered' : '0 Business Rule Covered';
  }

  handleTraceabilityClick(row: any, index: number): void {
    const storyKey = this.getTraceabilityStoryKey(row);
    this.actionEmit.emit({
      type: 'open-traceability',
      data: {
        traceability: row?.traceability,
        key: storyKey,
      },
      index,
    });
  }

  onAddToSuiteClick(): void {
    const selectedData = this.data?.rows?.filter((_: any, i: number) => this.selectedRows.has(i)) || [];
    this.actionEmit.emit({ type: 'add_to_suite', data: selectedData });
  }

  getPriorityLabel(priority: string): string {
    switch (priority?.toUpperCase()) {
      case 'P1':
        return 'CRITICAL';
      case 'P2':
        return 'HIGH';
      case 'P3':
        return 'MEDIUM';
      case 'P4':
        return 'LOW';
      default:
        return priority || 'MEDIUM';
    }
  }

  formatArrayValue(value: any): string {
    if (Array.isArray(value)) {
      return value.map((item, index) => `${index + 1}. ${item}`).join('\n');
    }
    return value;
  }

  getPaddedCount(count: number): string {
    return count.toString().padStart(2, '0');
  }

  /**
   * Split expanded-field strings into a list only when items are prefixed with
   * `#`, `*`, or a number (e.g. `"#abc , #123"`, `"*abc *123"`, `"1abc , 2xyz"`).
   */
  getProcessedContent(value: string): { type: 'numbered' | 'bullets' | 'plain', items: string[] } {
    if (!this.isValidString(value)) {
      return { type: 'plain', items: [] };
    }

    const delimiter = this.getListDelimiter(value);
    if (delimiter === '#' || delimiter === '*') {
      return { type: 'bullets', items: this.splitOnMarker(value, delimiter) };
    }
    if (delimiter === 'number') {
      return { type: 'numbered', items: this.splitOnNumberPrefixes(value) };
    }

    return { type: 'plain', items: [value] };
  }

  private isValidString(value: string): boolean {
    return !!(value && typeof value === 'string');
  }

  /** Item boundary: start of string, comma, or newline. */
  private readonly itemBoundary = '(?:^|,|\\n|\\r\\n)\\s*';

  private getListDelimiter(value: string): '#' | '*' | 'number' | null {
    const hashCount = this.countPrefixes(value, '#');
    const starCount = this.countPrefixes(value, '\\*');
    const numberCount = this.countPrefixes(value, '\\d+[.)]?');
    const trimmed = value.trim();

    if (hashCount > 0 && (trimmed.startsWith('#') || hashCount >= 2)) {
      return '#';
    }
    if (starCount > 0 && (trimmed.startsWith('*') || starCount >= 2)) {
      return '*';
    }
    if (numberCount >= 2 && /^\d/.test(trimmed)) {
      return 'number';
    }
    return null;
  }

  private countPrefixes(value: string, marker: string): number {
    // For number markers use newline/start boundary only to avoid false positives
    // from numbers inside parentheses like "(e.g., 5)".
    const boundary = marker.startsWith('\\d')
      ? '(?:^|\\n|\\r\\n)\\s*'
      : this.itemBoundary;
    const matches = value.match(new RegExp(`${boundary}${marker}`, 'gm'));
    return matches ? matches.length : 0;
  }

  private splitOnMarker(value: string, marker: '#' | '*'): string[] {
    return value
      .split(marker)
      .map(item => this.cleanListItem(item))
      .filter(item => item.length > 0);
  }

  private splitOnNumberPrefixes(value: string): string[] {
    // Use newline/start-of-string boundary only — commas must NOT be boundaries
    // because they appear naturally inside sentences like "(e.g., 5)."
    // Also require the separator character (. or )) to be present so plain
    // numbers embedded in text (e.g. "5 items") are never treated as list markers.
    const lineOnlyBoundary = '(?:^|\\n|\\r\\n)\\s*';
    return value
      .split(new RegExp(`${lineOnlyBoundary}\\d+[.)]\\s*`, 'm'))
      .map(item => this.cleanListItem(item))
      .filter(item => item.length > 0);
  }

  private cleanListItem(item: string): string {
    return item.replace(/^[\s,]+|[\s,]+$/g, '').trim();
  }
}

